/**
 * ExecutiveSummaryCard.jsx — Boardroom-grade decision card for any module report.
 *
 * Shows, in a single panel:
 *   - One-sentence conclusion (the headline)
 *   - Top friction / standout group (or any "watch this segment" line)
 *   - Recommended next move (short imperative)
 *   - Reliability badge (uses shared ReliabilitySummary in compact mode)
 *
 * Used at the top of HRLabReport (PeopleLab) and reusable for MarketLab/PharmaLab.
 *
 * Props:
 *   - conclusion (string, required)        — one-line headline
 *   - frictionLabel (string, optional)     — e.g. "Operations · −22pp vs baseline"
 *   - frictionDetail (string, optional)    — context for the friction line
 *   - nextMove (string, optional)          — recommended action
 *   - tone ('positive'|'neutral'|'negative'|'warning')
 *   - simulation (sim object, optional)    — used by ReliabilitySummary
 *   - preData (optional)
 *   - peopleLabStability (optional)
 *   - diagnostics (optional)
 *   - reliabilitySummary (optional)        — pre-computed summary to avoid recompute
 */
import { Card, CardContent } from '../ui/card';
import { Sparkles, Target as TargetIcon, ArrowRightCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import ReliabilitySummary from './ReliabilitySummary';

const TONE_RING = {
  positive: 'border-positive-border  bg-gradient-to-br from-emerald-50/60 via-white to-white dark:from-emerald-500/5 dark:via-card dark:to-card',
  neutral: 'border-warning-border  bg-gradient-to-br from-amber-50/60 via-white to-white dark:from-amber-500/5 dark:via-card dark:to-card',
  negative: 'border-negative-border  bg-gradient-to-br from-red-50/60 via-white to-white dark:from-red-500/5 dark:via-card dark:to-card',
  warning: 'border-warning-border  bg-gradient-to-br from-amber-50/60 via-white to-white dark:from-amber-500/5 dark:via-card dark:to-card',
  default: 'border-slate-200 dark:border-border bg-white dark:bg-card',
};

const TONE_LABEL = {
  positive: 'text-positive-soft-foreground ',
  neutral: 'text-warning-soft-foreground ',
  negative: 'text-negative-soft-foreground ',
  warning: 'text-warning-soft-foreground ',
  default: 'text-slate-600 dark:text-slate-300',
};

export default function ExecutiveSummaryCard({
  conclusion,
  frictionLabel = null,
  frictionDetail = null,
  nextMove = null,
  tone = 'default',
  simulation = null,
  preData = null,
  peopleLabStability = null,
  diagnostics = null,
  reliabilitySummary = null,
  badgeLabel = 'Executive Summary',
  className,
  testId = 'executive-summary-card',
}) {
  if (!conclusion) return null;

  const ring = TONE_RING[tone] || TONE_RING.default;
  const labelTone = TONE_LABEL[tone] || TONE_LABEL.default;

  return (
    <Card className={cn('border-2 shadow-sm', ring, className)} data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0 space-y-3">
            {/* Eyebrow */}
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-warning" />
              <span className={cn('text-[10px] font-semibold uppercase tracking-wider', labelTone)}>
                {badgeLabel}
              </span>
            </div>

            {/* Conclusion (headline) */}
            <p
              className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white leading-snug tracking-tight"
              data-testid={`${testId}-conclusion`}
            >
              {conclusion}
            </p>

            {/* Friction + Next move grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {(frictionLabel || frictionDetail) && (
                <div
                  className="flex items-start gap-2.5 p-3 rounded-lg bg-white/60 dark:bg-white/[0.02] border border-slate-200/70 dark:border-border"
                  data-testid={`${testId}-friction`}
                >
                  <TargetIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-negative" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                      Top Friction Group
                    </p>
                    {frictionLabel && (
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mt-0.5 truncate" title={frictionLabel}>
                        {frictionLabel}
                      </p>
                    )}
                    {frictionDetail && (
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                        {frictionDetail}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {nextMove && (
                <div
                  className="flex items-start gap-2.5 p-3 rounded-lg bg-white/60 dark:bg-white/[0.02] border border-slate-200/70 dark:border-border"
                  data-testid={`${testId}-next-move`}
                >
                  <ArrowRightCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                      Recommended Next Move
                    </p>
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mt-0.5 leading-snug">
                      {nextMove}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Reliability badge (compact) */}
          <div className="flex-shrink-0" data-testid={`${testId}-reliability`}>
            <ReliabilitySummary
              mode="compact"
              simulation={simulation}
              preData={preData}
              peopleLabStability={peopleLabStability}
              diagnostics={diagnostics}
              summary={reliabilitySummary}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
